import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { authController } from '../controllers/authController';
import Login from './auth/Login';
import Register from './auth/Register';
import EnquiryForm from './auth/EnquiryForm';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showEnquiryForm, setShowEnquiryForm] = useState(false);
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();

  // Check mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Check authentication on component mount
  useEffect(() => {
    const checkAuth = () => {
      if (authController.isAuthenticated()) {
        const currentUser = authController.getCurrentUser();
        setUser(currentUser);
        setIsAuthenticated(true);
      }
    };
    checkAuth();
  }, []);

  // Handle login success
  const handleLoginSuccess = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    setShowLogin(false);
    navigate('/dashboard');
  };

  // Handle register success
  const handleRegisterSuccess = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    setShowRegister(false);
    navigate('/dashboard');
  };

  // Handle enquiry success
  const handleEnquirySuccess = (data) => {
    console.log('Enquiry submitted:', data);
    alert(`✅ ${data.message}\n\nEnquiry ID: ${data.enquiryId}\n\nThank you for your interest! Our team will contact you within 24 hours.`);
  };

  // Handle logout
  const handleLogout = () => {
    authController.logout();
    setUser(null);
    setIsAuthenticated(false);
    navigate('/');
  };

  // Navigate to section
  const navigateToSection = (sectionId) => {
    if (location.pathname !== '/') {
      // If not on home page, navigate to home with section
      navigate(`/?section=${sectionId}`);
    } else {
      // If on home page, just scroll
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Update URL without hash
        window.history.pushState({}, '', `/?section=${sectionId}`);
      }
    }
    setIsOpen(false);
  };

  const styles = {
    navbar: {
      backgroundColor: '#ffffff',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
    },
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 20px',
    },
    navContent: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      height: '64px',
    },
    logo: {
      fontSize: '28px',
      fontWeight: 'bold',
      color: '#2563eb',
      cursor: 'pointer',
      textDecoration: 'none',
    },
    navLinks: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
    },
    link: {
      color: '#374151',
      fontWeight: '500',
      fontSize: '16px',
      transition: 'color 0.3s ease',
      cursor: 'pointer',
      textDecoration: 'none',
    },
    button: {
      backgroundColor: '#2563eb',
      color: '#ffffff',
      padding: '10px 24px',
      borderRadius: '8px',
      border: 'none',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'background-color 0.3s ease',
    },
    loginButton: {
      backgroundColor: 'transparent',
      color: '#2563eb',
      padding: '10px 24px',
      borderRadius: '8px',
      border: '2px solid #2563eb',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    logoutButton: {
      backgroundColor: '#dc2626',
      color: '#ffffff',
      padding: '10px 24px',
      borderRadius: '8px',
      border: 'none',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'background-color 0.3s ease',
    },
    userInfo: {
      color: '#374151',
      fontSize: '14px',
      fontWeight: '500',
    },
    mobileButton: {
      backgroundColor: 'transparent',
      border: 'none',
      cursor: 'pointer',
      padding: '5px',
      display: isMobile ? 'block' : 'none',
    },
    mobileMenu: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      padding: '20px',
      backgroundColor: '#ffffff',
      borderTop: '1px solid #e5e7eb',
    },
  };

  const getCurrentSection = () => {
    const params = new URLSearchParams(location.search);
    return params.get('section');
  };

  const isActive = (sectionId) => {
    return getCurrentSection() === sectionId;
  };

  return (
    <>
      <nav style={styles.navbar}>
        <div style={styles.container}>
          <div style={styles.navContent}>
            <Link 
              to="/"
              style={styles.logo}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              Tracklio Hub
            </Link>
            
            {/* Desktop Navigation */}
            {!isMobile && (
              <div style={styles.navLinks}>
                <div 
                  style={{
                    ...styles.link,
                    color: isActive('services') ? '#2563eb' : '#374151'
                  }}
                  onClick={() => navigateToSection('services')}
                  onMouseEnter={(e) => e.target.style.color = '#2563eb'}
                  onMouseLeave={(e) => {
                    if (!isActive('services')) {
                      e.target.style.color = '#374151';
                    }
                  }}
                >
                  Services
                </div>
                <div 
                  style={{
                    ...styles.link,
                    color: isActive('tech') ? '#2563eb' : '#374151'
                  }}
                  onClick={() => navigateToSection('tech')}
                  onMouseEnter={(e) => e.target.style.color = '#2563eb'}
                  onMouseLeave={(e) => {
                    if (!isActive('tech')) {
                      e.target.style.color = '#374151';
                    }
                  }}
                >
                  Technology
                </div>
                <div 
                  style={{
                    ...styles.link,
                    color: isActive('cta') ? '#2563eb' : '#374151'
                  }}
                  onClick={() => navigateToSection('cta')}
                  onMouseEnter={(e) => e.target.style.color = '#2563eb'}
                  onMouseLeave={(e) => {
                    if (!isActive('cta')) {
                      e.target.style.color = '#374151';
                    }
                  }}
                >
                  Contact
                </div>
                
                {isAuthenticated ? (
                  <>
                    <Link 
                      to="/dashboard"
                      style={styles.link}
                      onMouseEnter={(e) => e.target.style.color = '#2563eb'}
                      onMouseLeave={(e) => e.target.style.color = '#374151'}
                    >
                      Dashboard
                    </Link>
                    <span style={styles.userInfo}>
                      Hi, {user?.name || 'User'}
                    </span>
                    <button 
                      style={styles.logoutButton}
                      onClick={handleLogout}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#b91c1c'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#dc2626'}
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <button 
                      style={styles.loginButton}
                      onClick={() => setShowLogin(true)}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#2563eb';
                        e.target.style.color = '#ffffff';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = '#2563eb';
                      }}
                    >
                      Login
                    </button>
                    <button 
                      style={styles.button}
                      onClick={() => setShowEnquiryForm(true)}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#1d4ed8'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#2563eb'}
                    >
                      Get Started
                    </button>
                  </>
                )}
              </div>
            )}
            
            {/* Mobile Menu Button */}
            <button 
              style={styles.mobileButton}
              onClick={() => setIsOpen(!isOpen)}
            >
              <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="#374151">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
          
          {/* Mobile Navigation Menu */}
          {isOpen && isMobile && (
            <div style={styles.mobileMenu}>
              <div 
                style={styles.link} 
                onClick={() => navigateToSection('services')}
              >
                Services
              </div>
              <div 
                style={styles.link} 
                onClick={() => navigateToSection('tech')}
              >
                Technology
              </div>
              <div 
                style={styles.link} 
                onClick={() => navigateToSection('cta')}
              >
                Contact
              </div>
              
              {isAuthenticated ? (
                <>
                  <Link 
                    to="/dashboard"
                    style={styles.link}
                    onClick={() => setIsOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <span style={styles.userInfo}>Hi, {user?.name || 'User'}</span>
                  <button 
                    style={{...styles.logoutButton, width: '100%'}}
                    onClick={handleLogout}
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <button 
                    style={{...styles.loginButton, width: '100%'}}
                    onClick={() => {
                      setShowLogin(true);
                      setIsOpen(false);
                    }}
                  >
                    Login
                  </button>
                  <button 
                    style={{...styles.button, width: '100%'}}
                    onClick={() => {
                      setShowEnquiryForm(true);
                      setIsOpen(false);
                    }}
                  >
                    Get Started
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Login Modal */}
      <Login
        isOpen={showLogin} 
        onClose={() => setShowLogin(false)}
        onSwitchToRegister={() => {
          setShowLogin(false);
          setShowRegister(true);
        }}
        onLoginSuccess={handleLoginSuccess}
      />

      {/* Register Modal */}
      <Register 
        isOpen={showRegister} 
        onClose={() => setShowRegister(false)}
        onSwitchToLogin={() => {
          setShowRegister(false);
          setShowLogin(true);
        }}
        onRegisterSuccess={handleRegisterSuccess}
      />

      {/* Enquiry Form Modal */}
      <EnquiryForm 
        isOpen={showEnquiryForm} 
        onClose={() => setShowEnquiryForm(false)}
        onEnquirySuccess={handleEnquirySuccess}
      />
    </>
  );
};

export default Navbar;